// Talent-manager shortlist additions, on a project's Sellers tab. A TM adds a seller
// by username; the backend contacts them via concierge and hard-pins their offer into
// the client-facing top-3 IF it clears JUNO's quality bar (migration 0116). Gated
// `talent_management` server-side — an explorer-only viewer sees the list but a write 403s.

import { Badge, Button, Input } from "@recruiter/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { adminKeys, shortlistQuery } from "../lib/queries";
import { ApiError, addShortlistSeller, removeShortlistSeller } from "../lib/api";

export function ShortlistManager({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const rows = useQuery(shortlistQuery(projectId));
  const [username, setUsername] = useState("");
  const [error, setError] = useState<string | null>(null);

  function invalidate() {
    void qc.invalidateQueries({ queryKey: adminKeys.shortlist(projectId) });
    void qc.invalidateQueries({ queryKey: adminKeys.explorerProject(projectId) });
  }

  const add = useMutation({
    mutationFn: (name: string) =>
      addShortlistSeller(projectId, { username: name, fiverrSellerId: "", addedBy: "", note: "" }),
    onSuccess: () => {
      setUsername("");
      setError(null);
      invalidate();
    },
    onError: (e) => setError(e instanceof ApiError ? e.detail : "Could not add seller."),
  });

  const remove = useMutation({
    mutationFn: (rowId: number) => removeShortlistSeller(projectId, rowId),
    onSuccess: invalidate,
    onError: (e) => setError(e instanceof ApiError ? e.detail : "Could not remove seller."),
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const name = username.trim().replace(/^@/, "");
    if (name) add.mutate(name);
  }

  const active = (rows.data?.rows ?? []).filter((r) => r.active);

  return (
    <section className="sl-mgr">
      <div className="sl-head">
        <h3>Shortlist additions</h3>
        <p className="sl-sub">
          Add a seller and Mira contacts them for this project. When their offer comes back and
          clears the quality bar, it is pinned to the top of the client's choices.
        </p>
      </div>
      <form className="sl-add" onSubmit={onSubmit}>
        <Input
          type="text"
          inputSize="sm"
          placeholder="Fiverr username"
          aria-label="Seller username to add"
          value={username}
          onChange={(ev) => setUsername(ev.target.value)}
        />
        <Button
          variant="outline"
          tone="neutral"
          size="sm"
          disabled={add.isPending || !username.trim()}
        >
          {add.isPending ? "Adding…" : "Add seller"}
        </Button>
      </form>
      {error ? <p className="sl-err">{error}</p> : null}
      {active.length === 0 ? (
        <p className="sl-empty">No sellers added yet.</p>
      ) : (
        <ul className="sl-list">
          {active.map((r) => (
            <li key={r.id} className="sl-row">
              <a
                className="sl-name"
                href={`https://www.fiverr.com/${encodeURIComponent(r.username)}`}
                target="_blank"
                rel="noopener"
              >
                {r.username || r.fiverrSellerId}
              </a>
              {r.addedBy ? (
                <Badge tone="neutral" size="sm">
                  {r.addedBy}
                </Badge>
              ) : null}
              <Button
                variant="ghost"
                tone="danger"
                size="sm"
                disabled={remove.isPending}
                onClick={() => remove.mutate(r.id)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
